import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import PublicIcon from '@mui/icons-material/Public';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SEOHead from '../components/SEOHead';

function QuienesSomos() {
  return (
    <>
      <SEOHead
        title="Quiénes Somos - VoyBus"
        description="Somos un equipo que ama viajar y crear experiencias inolvidables. Agencia de viajes uruguaya especializada en excursiones grupales y viajes en bus. Con cercanía, organización y precios accesibles."
        keywords={['VoyBus', 'agencia de viajes Uruguay', 'quienes somos', 'equipo de viajes']}
        url="/quienes-somos"
        type="website"
      />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Título Principal */}
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            mb: 2,
            color: 'text.primary',
          }}
        >
          🚌 Quiénes Somos
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          Somos un equipo que ama viajar, conectar personas y crear experiencias que se disfrutan desde el primer
          kilómetro. Nacimos con una idea simple: que viajar en grupo sea fácil, accesible, seguro y, sobre todo…{' '}
          <strong>divertido</strong>.
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
          Creemos que los mejores recuerdos no vienen solo del destino, sino de todo lo que pasa en el camino: las
          charlas, las risas, la música, los paisajes y la gente que conocés.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* Nuestra manera de viajar */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FavoriteIcon sx={{ fontSize: '2rem', color: '#ff6b6b' }} />
            Nuestra manera de viajar
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
            Hay muchas formas de viajar, pero elegimos esta:
          </Typography>

          <Grid container spacing={2}>
            {[
              { icon: <EmojiPeopleIcon />, text: 'Con cercanía, trato humano y buena onda' },
              { icon: <CheckCircleIcon />, text: 'Con organización, seguridad y responsabilidad' },
              { icon: <DirectionsBusIcon />, text: 'Con itinerarios pensados para disfrutar sin complicarse' },
              { icon: <HandshakeIcon />, text: 'Con precios accesibles y alternativas para distintos estilos de viaje' },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        fontSize: '2rem',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="body1" sx={{ mt: 3, lineHeight: 1.8, fontStyle: 'italic', textAlign: 'center' }}>
            Nos importa que cada persona que viaja con nosotros sienta que está en buenas manos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Lo que nos mueve */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PublicIcon sx={{ fontSize: '2rem', color: '#42a5f5' }} />
            Lo que nos mueve
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 500 }}>
            Viajar transforma, inspira, une… y hace bien.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Nos mueve ese momento en el que ves a alguien disfrutar de una experiencia que tal vez había soñado por
            años.
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Nuestro propósito es acercar el viaje a más personas, sin importar si es su primera escapada, un viaje en
            grupo o unas vacaciones soñadas.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Nuestra esencia */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <HandshakeIcon sx={{ fontSize: '2rem', color: '#66bb6a' }} />
            Nuestra esencia
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontWeight: 500 }}>
            Lo que más nos importa es la experiencia humana.
          </Typography>

          <Grid container spacing={2}>
            {[
              'Relaciones honestas y transparentes',
              'Cercanía y acompañamiento genuino',
              'Empatía con cada pasajero',
              'Un equipo que se toma en serio los detalles, sin perder la sonrisa',
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 2,
                    borderLeft: '4px solid #66bb6a',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="body1" sx={{ mt: 3, lineHeight: 1.8, fontStyle: 'italic', textAlign: 'center', fontWeight: 600 }}>
            No buscamos "vender viajes". Buscamos que la gente viva experiencias.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Un equipo en movimiento */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <RocketLaunchIcon sx={{ fontSize: '2rem', color: '#ab47bc' }} />
            Un equipo en movimiento
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Somos jóvenes, dinámicos, y estamos creciendo.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Nos rodeamos de coordinadores/as, guías, colaboradores y partners que comparten valores y ganas de hacer
            las cosas bien.
          </Typography>

          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Creemos en mejorar, aprender, escuchar y evolucionar — como equipo y como empresa.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ¿Qué podés esperar? */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AutoStoriesIcon sx={{ fontSize: '2rem', color: '#ff9800' }} />
            ¿Qué podés esperar si viajás con nosotros?
          </Typography>

          <Card
            sx={{
              backgroundColor: '#fff3e0',
              border: '2px solid #ff9800',
              borderRadius: 2,
              p: 4,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.9,
                fontWeight: 500,
                textAlign: 'center',
                mb: 2,
              }}
            >
              Que te sientas acompañado desde el momento en que consultás, hasta que volvés a casa.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                fontStyle: 'italic',
                mb: 2,
              }}
            >
              Queremos que cuando bajés del bus digas:
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: 'primary.main',
              }}
            >
              "Esto lo volvería a hacer."
            </Typography>
          </Card>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default QuienesSomos;
